import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Avatar } from './AvatarGroup';
import ProjectTaskDetail from './ProjectTaskDetail';
import TaskStatusDropdown from './TaskStatusDropdown';
import CustomButton from '../OnboardingComponents/CustomButton';
import CustomCheckbox from '../OnboardingComponents/CustomCheckbox';
import EmptyState from '../Common/EmptyState';
import TruncatedText from '../Common/TruncatedText';
import StartSessionDropdown from './StartSessionDropdown';
import { ICONS } from '../../constants/icons';
import {
  resolveTaskSessionAction,
  taskSessionActionLabel,
} from '../../helpers/session';
import { celebrateTaskCompleted } from './TaskCompletedCelebration';

const SKELETON_ROWS = 6;
const SKELETON_CELLS = 6;

const STAT_ITEMS = [
  { key: 'total', label: 'Total Tasks' },
  { key: 'completed', label: 'Completed' },
  { key: 'inProgress', label: 'In Progress' },
  { key: 'notStarted', label: 'Not Started' },
  { key: 'hoursLogged', label: 'Hours Logged' },
];

/**
 * ProjectMyTasksTable
 * ------------------------------------------------------------------
 * Flat My Tasks table matching Figma 40004314-9104:
 * owner header + stats, then
 * Task Name | Status | Due Date | Estimated Hours | Logged Hrs | Actions
 */
const ProjectMyTasksTable = ({
  tasks = [],
  isLoading = false,
  error = null,
  playIconSrc = ICONS.play,
  checkIconSrc = ICONS.checkGreen,
  owner = null,
  stats = null,
  selectedTask = null,
  onSelectTask,
  onCloseTask,
  onAddTask,
  onStartSession,
  onStatusChange,
  onCollaboratorsAdded,
  onTaskUpdated,
  onTaskDeleted,
  plusIconSrc = ICONS.plus,
}) => {
  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserId = currentUser?.id ? String(currentUser.id) : '';

  const [checked, setChecked] = useState(new Set());
  const roleTone = owner?.roleTone || 'purple';

  useEffect(() => {
    setChecked(new Set(tasks.filter((task) => task.completed).map((task) => task.id)));
  }, [tasks]);

  const isCurrentUserTaskCollaborator = (task) =>
    currentUserId &&
    (task.collaborators || []).some(
      (c) => String(c.userId || c.user?.id || c.id) === currentUserId
    );

  const canEditTask = (task) =>
    Boolean(
      currentUserId &&
        onStatusChange &&
        (task.owner?.isYou ||
          String(task.owner?.id) === currentUserId ||
          isCurrentUserTaskCollaborator(task))
    );

  const toggleChecked = (task, nextChecked) => {
    if (!canEditTask(task)) return;
    const isDone = task.completed || task.status === 'Completed';
    const current = checked.has(task.id) || isDone;
    const isNowChecked =
      typeof nextChecked === 'boolean' ? nextChecked : !current;
    setChecked((prev) => {
      const next = new Set(prev);
      if (isNowChecked) next.add(task.id);
      else next.delete(task.id);
      return next;
    });
    if (isNowChecked) celebrateTaskCompleted();
    onStatusChange?.(task, isNowChecked ? 'Completed' : 'Not Started');
  };

  return (
    <div className="project-my-tasks">
      {owner ? (
        <div className="project-my-tasks__toolbar">
          <div className="project-my-tasks__owner">
            <Avatar
              src={owner.avatar}
              name={owner.name}
              size="md"
              className="project-my-tasks__owner-avatar"
            />
            <div className="project-my-tasks__owner-meta">
              <span className="project-my-tasks__owner-name">
                {owner.name}
                {owner.isYou ? ' (You)' : ''}
              </span>
              {owner.role ? (
                <span
                  className={`project-my-tasks__role project-my-tasks__role--${roleTone}`}
                >
                  {owner.role}
                </span>
              ) : null}
            </div>
          </div>

          {stats || onAddTask ? (
            <div className="project-my-tasks__toolbar-end">
              {stats ? (
                <div className="project-my-tasks__stats" aria-label="Task summary">
                  {STAT_ITEMS.map((item, index) => (
                    <React.Fragment key={item.key}>
                      {index > 0 ? (
                        <span className="project-my-tasks__stats-divider" aria-hidden="true" />
                      ) : null}
                      <div className="project-my-tasks__stat">
                        <span className="project-my-tasks__stat-value">
                          {stats[item.key] ?? 0}
                        </span>
                        <span className="project-my-tasks__stat-label">{item.label}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              ) : null}

              {onAddTask ? (
                <CustomButton
                  variant="primary"
                  size="sm"
                  trailingIconSrc={plusIconSrc}
                  trailingIconAlt=""
                  className="project-task-table__btn-add-task"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onAddTask();
                  }}
                >
                  Add Task
                </CustomButton>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="project-my-tasks__table" role="table" aria-label="My tasks">
        <div className="project-my-tasks__head" role="row">
          <div className="project-my-tasks__cell project-my-tasks__cell--name" role="columnheader">
            Task Name
          </div>
          <div className="project-my-tasks__cell project-my-tasks__cell--status" role="columnheader">
            Status
          </div>
          <div className="project-my-tasks__cell project-my-tasks__cell--date" role="columnheader">
            Due Date
          </div>
          <div className="project-my-tasks__cell project-my-tasks__cell--hours" role="columnheader">
            Estimated Hours
          </div>
          <div className="project-my-tasks__cell project-my-tasks__cell--hours" role="columnheader">
            Logged Hrs
          </div>
          <div className="project-my-tasks__cell project-my-tasks__cell--action" role="columnheader">
            Actions
          </div>
        </div>

        {isLoading &&
          Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="project-my-tasks__row project-my-tasks__row--skeleton"
              aria-hidden="true"
            >
              {Array.from({ length: SKELETON_CELLS }).map((__, cellIdx) => (
                <div key={cellIdx} className="project-my-tasks__skeleton-cell">
                  <div className="project-my-tasks__skeleton-bar" />
                </div>
              ))}
            </div>
          ))}

        {!isLoading && error && (
          <div className="project-my-tasks__empty">
            <EmptyState
              title="Unable to load tasks"
              description={error}
            />
          </div>
        )}

        {!isLoading && !error && tasks.length === 0 && (
          <div className="project-my-tasks__empty">
            <EmptyState
              title="No tasks yet"
              description="Add a task to get started."
              actionText={onAddTask ? 'Add Task' : undefined}
              actionTrailingIconSrc={onAddTask ? plusIconSrc : undefined}
              onActionClick={onAddTask}
              size="sm"
            />
          </div>
        )}

        {!isLoading &&
          !error &&
          tasks.map((task) => {
            const isDone = task.action === 'done' || task.completed;
            const isChecked = checked.has(task.id) || isDone;
            const sessionAction = resolveTaskSessionAction(task);

            return (
              <div key={task.id} className="project-my-tasks__row" role="row">
                <div className="project-my-tasks__cell project-my-tasks__cell--name" role="cell">
                  <CustomCheckbox
                    checked={isChecked}
                    onChange={({ target }) => toggleChecked(task, target.checked)}
                    checkIconSrc={checkIconSrc}
                    className="project-my-tasks__check"
                    disabled={!canEditTask(task)}
                    aria-label={
                      canEditTask(task)
                        ? `Mark ${task.title} complete`
                        : task.title
                    }
                  />
                  <TruncatedText
                    as="button"
                    type="button"
                    className="project-my-tasks__title"
                    title={task.title}
                    onClick={() => onSelectTask?.(task)}
                  >
                    {task.title}
                  </TruncatedText>
                </div>

                <div className="project-my-tasks__cell project-my-tasks__cell--status" role="cell">
                  <TaskStatusDropdown
                    value={task.status}
                    onChange={
                      canEditTask(task)
                        ? (nextStatus) => onStatusChange(task, nextStatus)
                        : undefined
                    }
                  />
                </div>

                <div className="project-my-tasks__cell project-my-tasks__cell--date" role="cell">
                  {task.dueDate}
                </div>

                <div className="project-my-tasks__cell project-my-tasks__cell--hours" role="cell">
                  {task.estimated}
                </div>

                <div className="project-my-tasks__cell project-my-tasks__cell--hours" role="cell">
                  {task.logged}
                </div>

                <div className="project-my-tasks__cell project-my-tasks__cell--action" role="cell">
                  {isDone ? (
                    <span className="project-my-tasks__done">Task Done</span>
                  ) : onStartSession ? (
                    <StartSessionDropdown
                      label={taskSessionActionLabel(sessionAction)}
                      playIconSrc={playIconSrc}
                      className="project-my-tasks__btn-session"
                      skipDropdown={sessionAction !== 'start'}
                      onSelect={(type) => onStartSession(task, type)}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}

        {onAddTask ? (
          <button
            type="button"
            className="project-my-tasks__add"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onAddTask();
            }}
          >
            <img src={plusIconSrc} alt="" aria-hidden="true" />
            Add task...
          </button>
        ) : null}
      </div>

      <ProjectTaskDetail
        task={selectedTask}
        owner={selectedTask?.owner || owner}
        onClose={() => onCloseTask?.()}
        onStartSession={
          onStartSession &&
          selectedTask &&
          selectedTask.action !== 'done' &&
          !selectedTask.completed
            ? (type) => {
                onStartSession(selectedTask, type);
                onCloseTask?.();
              }
            : undefined
        }
        onCollaboratorsAdded={onCollaboratorsAdded}
        onStatusChange={
          onStatusChange && selectedTask && canEditTask(selectedTask)
            ? (nextStatus) => onStatusChange(selectedTask, nextStatus)
            : undefined
        }
        onTaskUpdated={onTaskUpdated}
        onTaskDeleted={onTaskDeleted}
      />
    </div>
  );
};

export default ProjectMyTasksTable;
